import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react'
import { allDeploymentStatuses } from '@helo/shared'
import { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { Icon } from '../../../components/Icons/Icon'
import { AdminBadge } from '../../../components/StatusBadge/StatusBadge'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import type {
  CustomerDataApiDeployment,
  DeploymentStatus,
} from '../../../services/customerDataApiService'
import { formatAdminDateTime } from '../../../utils/dates'

export const statusColor: Record<DeploymentStatus, string> = {
  PENDING: 'blue',
  IN_PROGRESS: 'blue',
  SUCCESS: 'green',
  FAILED: 'red',
  CANCELED: 'gray',
}

export const isTerminalStatus = (s: DeploymentStatus): boolean =>
  s === 'SUCCESS' || s === 'FAILED' || s === 'CANCELED'

const getDeploymentStatusLabel = (deployment: CustomerDataApiDeployment) => {
  const logs = deployment.logs ?? ''
  if (deployment.status === 'CANCELED') {
    if (/superseded by newer deployment/i.test(logs)) return 'SUPERSEDED'
    return 'CANCELED'
  }
  if (deployment.status === 'FAILED') {
    if (/Failed to notify db-subgraphs/i.test(logs)) return 'FAILED TO START'
    if (/\[WATCHDOG\]/i.test(logs)) return 'TIMED OUT'
  }
  return deployment.status
}

const getDeploymentStatusHelp = (deployment: CustomerDataApiDeployment) => {
  const logs = deployment.logs ?? ''
  if (deployment.status === 'CANCELED') {
    if (/superseded by newer deployment/i.test(logs)) {
      return 'Canceled because a newer pipeline run replaced it.'
    }
    return 'Canceled manually before promotion.'
  }
  if (deployment.status === 'FAILED') {
    if (/Failed to notify db-subgraphs/i.test(logs)) {
      return 'The run was created in the API, but db-subgraphs never started it.'
    }
    if (/\[WATCHDOG\]/i.test(logs)) {
      return 'No progress was reported for too long.'
    }
  }
  return null
}

const shortId = (id: string) => (id.length > 8 ? id.slice(0, 8) + '…' : id)

const formatDuration = (
  startedAt: string | undefined,
  completedAt: string | null | undefined,
  status: DeploymentStatus,
): string => {
  if (!startedAt) return '—'
  const start = new Date(startedAt).getTime()
  const end = completedAt
    ? new Date(completedAt).getTime()
    : isTerminalStatus(status)
      ? start
      : Date.now()
  const ms = Math.max(0, end - start)
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const remS = s % 60
  if (m < 60) return `${m}m ${remS}s`
  const h = Math.floor(m / 60)
  return `${h}h ${m % 60}m`
}

interface StatusFilterPopoverProps {
  selected: DeploymentStatus[]
  onChange: (next: DeploymentStatus[]) => void
}

export const StatusFilterPopover = ({
  selected,
  onChange,
}: StatusFilterPopoverProps) => (
  <Popover closeOnBlur placement="bottom-start">
    <PopoverTrigger>
      <Button size="sm" variant="outline" data-testid="status-filter-button">
        Status
        {selected.length > 0 ? ` (${selected.length})` : ''}
      </Button>
    </PopoverTrigger>
    <PopoverContent maxW="240px" w="auto">
      <PopoverArrow />
      <PopoverBody>
        <CheckboxGroup
          value={selected}
          onChange={(next: string[]) => onChange(next as DeploymentStatus[])}
        >
          <Flex direction="column" gap="1">
            {allDeploymentStatuses.map(s => (
              <Checkbox key={s} value={s} size="sm">
                <AdminBadge colorScheme={statusColor[s]} mr="1">
                  {s}
                </AdminBadge>
              </Checkbox>
            ))}
          </Flex>
        </CheckboxGroup>
      </PopoverBody>
    </PopoverContent>
  </Popover>
)

interface ConfigIdFilterProps {
  value: string
  onChange: (next: string) => void
}

export const ConfigIdFilter = ({ value, onChange }: ConfigIdFilterProps) => (
  <Flex align="center" gap="2">
    <Text fontSize="xs" color="presets.neutral.70">
      Config id:
    </Text>
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder="paste a config id"
      style={{
        fontFamily: 'monospace',
        fontSize: '12px',
        padding: '4px 8px',
        borderRadius: '4px',
        border: '1px solid var(--chakra-colors-presets-neutral-30)',
        minWidth: '220px',
      }}
      data-testid="config-id-filter"
    />
  </Flex>
)

type DeploymentsColumnsDeps = {
  cancelMutation: (options: {
    variables: { deploymentId: string }
  }) => Promise<unknown>
  rerunMutation: (options: {
    variables: { deploymentId: string }
  }) => Promise<unknown>
  cancelMutationLoading: boolean
  rerunMutationLoading: boolean
}

export const useDeploymentsAdminColumns = ({
  cancelMutation,
  rerunMutation,
  cancelMutationLoading,
  rerunMutationLoading,
}: DeploymentsColumnsDeps) =>
  useMemo<ColumnDef<CustomerDataApiDeployment>[]>(
    () => [
      {
        accessorKey: 'startedAt',
        header: 'Started',
        cell: info => (
          <Text whiteSpace="nowrap">
            {formatAdminDateTime(info.getValue() as string) ?? '—'}
          </Text>
        ),
        meta: {
          toText: val => formatAdminDateTime(val as string) ?? '—',
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: info => {
          const d = info.row.original
          const status = info.getValue() as DeploymentStatus
          const statusLabel = getDeploymentStatusLabel(d)
          const statusHelp = getDeploymentStatusHelp(d)
          return (
            <Flex direction="column" align="flex-start" gap="1">
              <Flex align="center" gap="2">
                <AdminBadge colorScheme={statusColor[status]}>
                  {statusLabel}
                </AdminBadge>
                {d.isActive && (
                  <AdminBadge colorScheme="green">LIVE</AdminBadge>
                )}
              </Flex>
              {statusHelp && (
                <Text fontSize="10px" color="presets.neutral.70">
                  {statusHelp}
                </Text>
              )}
            </Flex>
          )
        },
        meta: { toText: (_, row) => getDeploymentStatusLabel(row) },
      },
      {
        id: 'attempt',
        header: 'Attempt',
        accessorFn: row => row.attempt ?? 1,
        cell: info => (
          <Text fontSize="sm">#{(info.getValue() as number) ?? 1}</Text>
        ),
        meta: {
          toText: val => `#${(val as number) ?? 1}`,
        },
      },
      {
        id: 'duration',
        header: 'Duration',
        accessorFn: row =>
          formatDuration(
            row.startedAt,
            row.completedAt,
            row.status as DeploymentStatus,
          ),
        cell: info => <Text fontSize="sm">{info.getValue() as string}</Text>,
        meta: { toText: val => val as string },
      },
      {
        accessorKey: 'configId',
        header: 'Config',
        cell: info => {
          const d = info.row.original
          const configId = info.getValue() as string
          const createdDate = (d as { config?: { createdDate?: string } })
            .config?.createdDate
          return (
            <Flex align="center" gap={2} className="link-cell">
              <Text fontFamily="mono" fontSize="xs">
                <Link
                  to={`/admin/customer-data-api-config?configId=${configId}`}
                  style={{
                    color: 'var(--chakra-colors-presets-turquoise-70)',
                  }}
                >
                  {createdDate
                    ? (formatAdminDateTime(createdDate) ?? shortId(configId))
                    : shortId(configId)}
                </Link>
              </Text>
              <Tooltip label="Open Link in New Tab" placement="top">
                <Box
                  as="a"
                  href={`/admin/customer-data-api-config?configId=${configId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  opacity={0}
                  _groupHover={{ opacity: 1 }}
                  transition="opacity 0.2s"
                  color="presets.turquoise.70"
                  display="flex"
                  alignItems="center"
                  onClick={e => e.stopPropagation()}
                >
                  <Icon icon="ExternalLinkIcon" boxSize="18px" />
                </Box>
              </Tooltip>
            </Flex>
          )
        },
        meta: {
          toText: (_, row) => {
            const configCreatedDate = (
              row as { config?: { createdDate?: string } }
            ).config?.createdDate
            return configCreatedDate
              ? (formatAdminDateTime(configCreatedDate) ?? row.configId)
              : row.configId
          },
        },
      },
      {
        id: 'triggeredBy',
        header: 'Triggered by',
        accessorFn: row =>
          row.deployedByUser?.name ??
          row.deployedByUser?.email ??
          row.deployedByUserId,
        meta: { toText: val => (val as string) || '-' },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: info => {
          const d = info.row.original
          const isInFlight = !isTerminalStatus(d.status as DeploymentStatus)
          return (
            <Flex justify="flex-end" w="full" gap="2">
              <Button
                as={Link}
                to={`/admin/deployments/${d.id}`}
                size="sm"
                variant="secondary"
                onClick={e => e.stopPropagation()}
              >
                View
              </Button>
              {isInFlight ? (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  onClick={e => {
                    e.stopPropagation()
                    void cancelMutation({ variables: { deploymentId: d.id } })
                  }}
                  isLoading={cancelMutationLoading}
                  data-testid={`cancel-deployment-${d.id}`}
                >
                  Cancel
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={e => {
                    e.stopPropagation()
                    void rerunMutation({ variables: { deploymentId: d.id } })
                  }}
                  isLoading={rerunMutationLoading}
                  data-testid={`rerun-deployment-${d.id}`}
                >
                  Re-run
                </Button>
              )}
            </Flex>
          )
        },
        meta: { toText: () => '' },
      },
    ],
    [
      rerunMutation,
      cancelMutation,
      rerunMutationLoading,
      cancelMutationLoading,
    ],
  )
